/**
 * For DDP 2.3 header change use
 * Inline navigation tabs
 */
/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import snakeCase from 'lodash/snakeCase';
import throttle from 'lodash/throttle';

import { Grid, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';

import Container from 'bundles/degree-description/components/shared/Container';
import { useDegreeDescriptionPageElementRefs } from 'bundles/degree-description/contexts/DegreeDescriptionPageElementRefsContext';
import useNavIntersectionObserver from 'bundles/degree-description/hooks/useNavIntersectionObserver';
import { useDegreeDescriptionEventingContext } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';
import type { NavigationItem } from 'bundles/degree-description/types';
import {
  buildNavigationLink,
  getCurrentNavItem,
  snakeCaseNavigationName,
} from 'bundles/degree-description/utils/utils';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';

import _t from 'i18n!nls/degree-description';

const NAV_CONTAINER_HEIGHT_PX = 60;

const styles = {
  container: css`
    min-height: 60px;
    margin: 0;
    ${breakpoints.up('md')} {
      margin: var(--cds-spacing-600) 0;
    }
  `,
  fixNavbar: css`
    position: var(--fix-navbar-position-style);
    top: var(--fix-navbar-top-style);
    width: 100%;
    background: var(--cds-color-neutral-background-primary);
    z-index: 10;
    transition: top 0.3s;
    box-shadow: var(--fix-navbar-box-shadow-style);
  `,
  withBoxShadow: css`
    box-shadow: 0 8px 12px -12px var(--cds-color-darken-200);
  `,
  navContainer: css`
    left: 0;
    width: 100%;
    height: ${NAV_CONTAINER_HEIGHT_PX}px;
    z-index: 10;
    display: flex;
    overflow-x: auto;
    justify-content: flex-start;
    align-items: flex-end;
    background: var(--cds-color-neutral-background-primary);
    ${breakpoints.down('sm')} {
      justify-content: flex-start;
      margin-top: 0;
      margin-bottom: 0;
    }
  `,
  navWrapper: css`
    display: flex;
    white-space: nowrap;
    margin-bottom: 0;
    padding-left: 0;
  `,
  navElement: css`
    list-style: none;
    padding: var(--cds-spacing-150) var(--cds-spacing-300);
    border-bottom: 2px solid var(--cds-color-grey-50);
  `,
  navElementSelected: css`
    border-bottom: 2px solid var(--cds-color-blue-800);
    color: var(--cds-color-blue-800);
    font-weight: 600;
  `,
  navLink: css`
    cursor: pointer;
    text-decoration: none;

    &:hover,
    &:visited,
    &:focus {
      text-decoration: none;
    }
  `,
  navText: css`
    font-size: 16px;
    color: var(--cds-color-neutral-primary);
  `,
  navTextSelected: css`
    color: var(--cds-color-blue-800);
  `,
};

type InlineNavigationProps = {
  navigationItems: NavigationItem[];
  parentNavRoute: string;
  currentNavRoute: string;
  slug: string;
  isLightweightDdp?: boolean;
};

const InlineNavigation = ({
  navigationItems,
  parentNavRoute,
  currentNavRoute,
  slug,
  isLightweightDdp,
}: InlineNavigationProps) => {
  const { setNavbarRef, navbarRef } = useDegreeDescriptionPageElementRefs();
  const [position, setPosition] = useState<string>('static');
  const [top, setTop] = useState<number>(0);
  const navContainerRef = useRef<HTMLDivElement | null>(null);
  const activeNavItemRef = useRef<HTMLLIElement | null>(null);
  const currentNavItem = getCurrentNavItem(navigationItems, currentNavRoute);
  const parentNavItem = parentNavRoute;
  const isDesktopView = useMediaQuery(breakpoints.up('md'));
  const [activeTab, setActiveTab] = useState(navigationItems[0]?.id);
  useNavIntersectionObserver(setActiveTab);
  const { trackClickPageTab } = useDegreeDescriptionEventingContext();

  const menuItems = useMemo(() => {
    return navigationItems.map((navigationItem, index) => {
      const linkHref = navigationItem.isIndexPage ? '' : `/${navigationItem.id}`;
      const href = isLightweightDdp
        ? `#${navigationItem.id}`
        : {
            pathname: buildNavigationLink(slug, linkHref, parentNavItem),
          };

      const isCurrentElement = navigationItem?.id === currentNavItem?.id;
      const isInSection = activeTab === navigationItem.id;
      const showSelected = isLightweightDdp ? isInSection : isCurrentElement;

      // Function to handle scrolling to each section for lightweight DDP
      const scrollToDiv = () => {
        const targetElement = document.getElementById(navigationItem.id);
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }

        if (navigationItem.id === 'overview') {
          history.replaceState({}, '', location.pathname);
        } else {
          history.pushState(null, '', `#${navigationItem.id}`);
        }
        setActiveTab(navigationItem.id);
      };

      const onClickHandle = (e: React.MouseEvent<HTMLAnchorElement>) => {
        trackClickPageTab(snakeCaseNavigationName(navigationItem.id));

        if (!isLightweightDdp) {
          return;
        }
        e.preventDefault();
        scrollToDiv();
      };

      return (
        <li
          key={navigationItem.id}
          css={[styles.navElement, showSelected && styles.navElementSelected]}
          data-testid="nav-element"
          ref={(e) => {
            if (showSelected) {
              activeNavItemRef.current = e;
            }
          }}
        >
          <TrackedLink2
            css={styles.navLink}
            id={`nav-title-inline-${index}`}
            trackingName={`navigation_${snakeCase(navigationItem.id)}`}
            href={href}
            onClick={onClickHandle}
            aria-current={showSelected ? 'page' : 'false'}
          >
            <Typography2
              variant="bodyPrimary"
              css={[styles.navText, showSelected && styles.navTextSelected]}
              component="span"
            >
              {navigationItem.name}
            </Typography2>
          </TrackedLink2>
        </li>
      );
    });
  }, [navigationItems, isLightweightDdp, slug, parentNavItem, currentNavItem?.id, activeTab, trackClickPageTab]);

  useEffect(() => {
    const onScrollHandler = throttle(() => {
      const refElementTop = navContainerRef?.current?.getBoundingClientRect().top ?? 0;
      const scrollYTrack = window.scrollY;
      if (isDesktopView) {
        if (scrollYTrack > 0 && refElementTop < 80) {
          setPosition('fixed');
          setTop(80);
        } else {
          setPosition('static');
          setTop(0);
        }
      } else if (refElementTop <= 0) {
        setPosition('fixed');
        setTop(0);
      } else {
        setPosition('static');
        setTop(0);
      }
    }, 50);

    window.addEventListener('scroll', onScrollHandler);
    return () => window.removeEventListener('scroll', onScrollHandler);
  }, [isDesktopView]);

  useEffect(() => {
    if (activeNavItemRef.current) {
      navbarRef?.scroll({ left: activeNavItemRef.current.offsetLeft });
    }
  }, [currentNavRoute, navbarRef, navigationItems]);

  // Set focus on navigation selected tab to prevent focus goes back up to the top of page
  useEffect(() => {
    if (currentNavRoute !== 'overview') {
      activeNavItemRef.current?.querySelector('a')?.focus();
    }
  });

  return (
    <div css={styles.container} ref={navContainerRef}>
      <div
        css={styles.fixNavbar}
        style={{
          ['--fix-navbar-position-style' as string]: position,
          ['--fix-navbar-top-style' as string]: `${top}px`,
          ['--fix-navbar-box-shadow-style' as string]:
            position === 'fixed' ? '0 8px 12px -12px var(--cds-color-darken-200)' : 'unset',
        }}
      >
        <Container>
          <Grid
            component="nav"
            data-testid="navigation"
            css={styles.navContainer}
            ref={(e: HTMLElement) => setNavbarRef?.(e)}
            aria-label={_t('Degree Description Page')}
          >
            <ul css={styles.navWrapper}>{menuItems}</ul>
          </Grid>
        </Container>
      </div>
    </div>
  );
};

export default InlineNavigation;
